import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, Sel, Toggle, Btn, Card, MediaPicker, Badge } from "../../components/admin/ui";

const KINDS = ["audio", "artwork", "booklet", "video", "other"];

function AssetRow({ a, idx, update, remove }) {
  const set = (k, v) => update(idx, { ...a, [k]: v });
  return (
    <div className="border border-white/10 p-4 grid md:grid-cols-4 gap-3 bg-[#0c0c0c]" data-testid={`vault-asset-editor-${a.assetId}`}>
      <TInput label="TITLE / LABEL" value={a.label} onChange={(v) => set("label", v)} />
      <TInput label="DESCRIPTION" value={a.description} onChange={(v) => set("description", v)} />
      <Sel label="TYPE" value={a.kind} onChange={(v) => set("kind", v)} options={KINDS.map((k) => ({ v: k, l: k.toUpperCase() }))} />
      <TInput label="FORMAT" value={a.format} onChange={(v) => set("format", v)} />
      <MediaPicker label="PROTECTED FILE (MEDIA ID)" value={a.mediaId} onChange={(v) => set("mediaId", v || null)} mode="id" testid={`asset-${a.assetId}-media`} />
      <TInput label="UNLOCK DATE (ISO, OPTIONAL)" value={a.unlockAt} onChange={(v) => set("unlockAt", v || null)} type="datetime-local" />
      <div className="flex items-end gap-5 pb-1 md:col-span-2">
        <Toggle label="AVAILABLE" checked={!!a.available} onChange={(v) => set("available", v)} testid={`asset-${a.assetId}-available`} />
        <Toggle label="NOTIFY ON RELEASE" checked={a.notifyOnRelease !== false} onChange={(v) => set("notifyOnRelease", v)} />
        <button onClick={() => remove(idx)} className="text-red-400/70 hover:text-red-400 text-xs ml-auto">REMOVE</button>
      </div>
    </div>
  );
}

function NotifyPanel() {
  const [digitalId, setDigitalId] = useState("dig-dimension-i-i-am");
  const [assetId, setAssetId] = useState("");
  const [result, setResult] = useState(null);
  const run = async (dryRun) => {
    try {
      const res = await adm.vaultNotify({ digitalId: digitalId.trim(), assetId: assetId.trim(), dryRun });
      setResult(res);
      toast.success(dryRun ? "Dry run complete." : `Sent ${res.sent} notification(s).`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mt-8" >
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-3">VAULT NOTIFICATIONS</p>
      <p className="text-white/40 text-xs mb-5">Entitlement-verified, deduped, opt-out-respecting. Always dry run first.</p>
      <div className="grid md:grid-cols-4 gap-4 items-end">
        <TInput label="DIGITAL RELEASE ID" value={digitalId} onChange={setDigitalId} testid="notify-digital-id" />
        <TInput label="ASSET ID" value={assetId} onChange={setAssetId} testid="notify-asset-id" />
        <Btn variant="ghost" onClick={() => run(true)} testid="notify-dry-run">DRY RUN</Btn>
        <Btn onClick={() => run(false)} testid="notify-send">SEND</Btn>
      </div>
      {result && (
        <p className="font-mono text-[9px] text-white/50 mt-4" data-testid="notify-result">
          {result.dry_run ? "DRY RUN — " : ""}ENTITLED: {result.entitled_emails} · SENT: {result.sent} · NO ACCOUNT: {result.skipped_no_account} · OPTED OUT: {result.skipped_opted_out} · DUPLICATES: {result.skipped_duplicates} · FAILED: {result.failed}
        </p>
      )}
    </Card>
  );
}

export default function AdminVault() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => adm.list("digital-releases").then(setItems).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (k, v) => setEditing((x) => ({ ...x, [k]: v }));
  const updateAsset = (idx, a) => setEditing((x) => ({ ...x, assets: x.assets.map((o, i) => (i === idx ? a : o)) }));
  const removeAsset = (idx) => setEditing((x) => ({ ...x, assets: x.assets.filter((_, i) => i !== idx) }));
  const addAsset = () => setEditing((x) => ({ ...x, assets: [...(x.assets || []), {
    assetId: `asset-${Date.now()}`, label: "New Asset", kind: "audio", format: "", description: "",
    available: false, notifyOnRelease: true, mediaId: null, storageKey: null, filename: null, unlockAt: null, status: "active" }] }));

  const save = async () => {
    try {
      const { _id, digitalId, source, createdAt, updatedAt, ...body } = editing;
      if (digitalId && items.find((i) => i.digitalId === digitalId)) await adm.update("digital-releases", digitalId, body);
      else await adm.create("digital-releases", { ...body, digitalId });
      toast.success("Vault release saved.");
      setEditing(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-vault">
      <PageHead title="Vault" sub="Attach real digital assets to owned releases. Protected files are only delivered after entitlement verification — never by public URL.">
        <Btn onClick={() => setEditing({ digitalId: `dig-${Date.now()}`, title: "", type: "album", dimension: null, artwork: "", grantedByLookupKeys: [], assets: [] })} testid="vault-new">NEW VAULT RELEASE</Btn>
      </PageHead>

      {editing && (
        <Card className="mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            <TInput label="DIGITAL ID" value={editing.digitalId} onChange={(v) => set("digitalId", v)} />
            <TInput label="TITLE" value={editing.title} onChange={(v) => set("title", v)} testid="vault-title" />
            <Sel label="TYPE" value={editing.type} onChange={(v) => set("type", v)} options={[{ v: "album", l: "ALBUM" }, { v: "track", l: "TRACK" }]} />
            <TInput label="ENTITLED BY LOOKUP KEYS (COMMA)" value={(editing.grantedByLookupKeys || []).join(",")}
              onChange={(v) => set("grantedByLookupKeys", v.split(",").map((s) => s.trim()).filter(Boolean))} testid="vault-lookup-keys" />
            <MediaPicker label="ARTWORK" value={editing.artwork} onChange={(v) => set("artwork", v)} mode="url" filterType="image" />
          </div>
          <div className="flex items-center justify-between mt-6 mb-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-gold">ASSETS</p>
            <Btn variant="ghost" onClick={addAsset} testid="vault-add-asset">ADD ASSET</Btn>
          </div>
          <div className="space-y-3">
            {(editing.assets || []).map((a, i) => <AssetRow key={a.assetId} a={a} idx={i} update={updateAsset} remove={removeAsset} />)}
          </div>
          <div className="flex gap-3 mt-5">
            <Btn onClick={save} testid="vault-save">SAVE</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)}>CANCEL</Btn>
          </div>
        </Card>
      )}

      {items.map((r) => (
        <div key={r.digitalId} className="flex items-center justify-between gap-4 border-t border-white/10 py-4 px-2" data-testid={`vault-row-${r.digitalId}`}>
          <div className="flex items-center gap-4 min-w-0">
            {r.artwork && <img src={r.artwork} alt="" className="w-10 h-10 object-cover" />}
            <div>
              <p className="text-white/90">{r.title}</p>
              <p className="font-mono text-[9px] text-white/40 mt-0.5">{r.digitalId} · {(r.assets || []).length} ASSETS · {(r.assets || []).filter((a) => a.available).length} LIVE</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone="dim">{(r.grantedByLookupKeys || []).join(", ") || "NO GRANTS"}</Badge>
            <Btn variant="ghost" onClick={() => setEditing(r)} testid={`vault-edit-${r.digitalId}`}>EDIT</Btn>
          </div>
        </div>
      ))}

      <NotifyPanel />
    </div>
  );
}
